import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchOddsScreen, ODDS_SCREEN_PAGE_SIZE } from '../api/oddsScreenApi';

const DEFAULT_PAGE_SIZE = ODDS_SCREEN_PAGE_SIZE;

/**
 * Infinite loader for the /odds_screen endpoint, paginated at the game level.
 *
 * options:
 * - league, market, outcomeIdNormalizedNoBook, isLive: odds filters
 * - pageSize: games per page (defaults to ODDS_SCREEN_PAGE_SIZE, clamped in fetchOddsScreen)
 * - accessToken: auth token for backend
 * - refreshKey: optional key (e.g. refreshCounter) to force reload even if filters are unchanged
 */
export function useInfiniteOddsScreen(options) {
	const {
		league,
		market,
		outcomeIdNormalizedNoBook,
		isLive,
		pageSize = DEFAULT_PAGE_SIZE,
		accessToken,
		refreshKey,
	} = options || {};

	const [items, setItems] = useState([]);
	const [page, setPage] = useState(1);
	const [hasMore, setHasMore] = useState(true);
	const [isInitialLoading, setIsInitialLoading] = useState(false);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [error, setError] = useState(null);
	const [pagination, setPagination] = useState(null);

	const initialFetchInFlightRef = useRef(false);

	const filtersKey = useMemo(
		() =>
			JSON.stringify({
				league: league || null,
				market: market || null,
				outcomeIdNormalizedNoBook: outcomeIdNormalizedNoBook || null,
				isLive: typeof isLive === 'boolean' ? isLive : null,
				refreshKey: refreshKey || 0,
			}),
		[league, market, outcomeIdNormalizedNoBook, isLive, refreshKey],
	);

	const previousFiltersKeyRef = useRef(null);
	const requestIdRef = useRef(0);

	const reset = useCallback(() => {
		requestIdRef.current += 1;
		setItems([]);
		setPage(1);
		setHasMore(true);
		setError(null);
		initialFetchInFlightRef.current = false;
		setIsInitialLoading(false);
		setIsLoadingMore(false);
		setPagination(null);
	}, []);

	// Reset when filters or refreshKey change
	useEffect(() => {
		if (previousFiltersKeyRef.current !== null && previousFiltersKeyRef.current !== filtersKey) {
			reset();
		}
		previousFiltersKeyRef.current = filtersKey;
	}, [filtersKey, reset]);

	const loadPage = useCallback(
		async (pageToLoad, isInitial) => {
			if (isInitial) {
				if (initialFetchInFlightRef.current) return;
				initialFetchInFlightRef.current = true;
			} else if (isLoadingMore || !hasMore) {
				return;
			}

			const currentRequestId = ++requestIdRef.current;

			if (isInitial) {
				setIsInitialLoading(true);
			} else {
				setIsLoadingMore(true);
			}
			setError(null);

			try {
				const { games, pagination: pageInfo } = await fetchOddsScreen({
					league,
					market,
					outcomeIdNormalizedNoBook,
					isLive,
					page: pageToLoad,
					limit: pageSize,
					accessToken,
				});

				if (requestIdRef.current !== currentRequestId) {
					return;
				}

				const safeGames = Array.isArray(games) ? games : [];
				setItems((prev) => (pageToLoad === 1 ? safeGames : [...prev, ...safeGames]));
				setPage(pageToLoad);
				setPagination(pageInfo || null);

				// Prefer explicit hasNext from the backend. Otherwise keep fetching
				// while we receive full pages (pagination.total is often understated).
				// Only stop on an empty page or hasNext === false.
				const limitForPage =
					typeof pageInfo?.limit === 'number' && pageInfo.limit > 0
						? pageInfo.limit
						: pageSize;

				let more = false;
				if (safeGames.length === 0) {
					more = false;
				} else if (pageInfo && typeof pageInfo.hasNext === 'boolean') {
					more = pageInfo.hasNext;
				} else if (safeGames.length >= limitForPage) {
					more = true;
				} else if (
					pageInfo &&
					typeof pageInfo.total === 'number' &&
					pageInfo.total > 0
				) {
					const totalPages = Math.ceil(pageInfo.total / limitForPage);
					more = pageToLoad < totalPages;
				} else {
					more = true;
				}

				setHasMore(more);
			} catch (e) {
				if (requestIdRef.current !== currentRequestId) {
					return;
				}
				setError(e instanceof Error ? e : new Error('Unknown error fetching odds screen data'));
			} finally {
				if (isInitial) {
					initialFetchInFlightRef.current = false;
				}
				if (requestIdRef.current === currentRequestId) {
					if (isInitial) {
						setIsInitialLoading(false);
					} else {
						setIsLoadingMore(false);
					}
				}
			}
		},
		[
			league,
			market,
			outcomeIdNormalizedNoBook,
			isLive,
			pageSize,
			accessToken,
			hasMore,
			isLoadingMore,
		],
	);

	// Initial load (and reload when filters/reset clear items)
	useEffect(() => {
		if (items.length === 0 && hasMore) {
			void loadPage(1, true);
		}
	}, [items.length, hasMore, loadPage]);

	const loadMore = useCallback(() => {
		if (!hasMore || isInitialLoading || isLoadingMore) return;
		const nextPage = page + 1;
		void loadPage(nextPage, false);
	}, [hasMore, isInitialLoading, isLoadingMore, loadPage, page]);

	return {
		items,
		isInitialLoading,
		isLoadingMore,
		hasMore,
		error,
		loadMore,
		reset,
		pagination,
		page,
	};
}

